import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FiDatabase, FiZap, FiTrendingUp, FiShield, FiBarChart, FiCpu, FiCheckCircle } from 'react-icons/fi';
import './AgenticDatabase.css';

const AgenticDatabase = () => {
  const features = [
    {
      icon: <FiDatabase />,
      title: "High-Performance Telemetry DB",
      description: "Industry's first high-performance telemetry database built for the agentic data model."
    },
    {
      icon: <FiZap />,
      title: "Agentic Data Model",
      description: "Designed specifically for agentic data patterns with optimized storage and retrieval."
    },
    {
      icon: <FiTrendingUp />,
      title: "Real-time Processing",
      description: "Process and analyze telemetry data in real-time with sub-millisecond latency."
    },
    {
      icon: <FiShield />,
      title: "Enterprise Security",
      description: "Built-in security features with encryption, access controls, and compliance."
    },
    {
      icon: <FiBarChart />,
      title: "Advanced Analytics",
      description: "Powerful analytics engine for complex queries and data insights."
    },
    {
      icon: <FiCpu />,
      title: "Scalable Architecture",
      description: "Horizontally scalable architecture to handle massive data volumes."
    }
  ];

  const benefits = [
    "Industry's first agentic telemetry database",
    "High-performance data processing and storage",
    "Real-time analytics with sub-millisecond latency",
    "Enterprise-grade security and compliance",
    "Horizontally scalable architecture",
    "Optimized for agentic data patterns"
  ];

  return (
    <div className="agentic-database-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <motion.div 
            className="hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1>Agentic Database</h1>
            <p className="hero-subtitle">
              The industry's first high-performance telemetry DB built for the agentic data model
            </p>
            <div className="hero-actions">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link to="/pricing" className="btn btn-primary">
                  Start Free Trial
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link to="/demo" className="btn btn-outline">
                  Request Demo
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Revolutionary Telemetry Database</h2>
            <p>Built from the ground up for agentic data patterns and high-performance telemetry</p>
          </motion.div>

          <div className="features-grid">
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                className="feature-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <div className="feature-icon">
                  {feature.icon}
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits">
        <div className="container">
          <div className="benefits-content">
            <motion.div 
              className="benefits-text"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2>Why Choose Our Agentic Database?</h2>
              <p>
                Our agentic database is purpose-built for modern telemetry workloads, 
                providing unprecedented performance and scalability for agentic data patterns.
              </p>
              <ul className="benefits-list">
                {benefits.map((benefit, index) => (
                  <motion.li 
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <FiCheckCircle />
                    {benefit}
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            <motion.div 
              className="benefits-visual"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="dashboard-preview">
                <div className="dashboard-header">
                  <div className="dashboard-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <h4>Database Dashboard</h4>
                </div>
                <div className="dashboard-content">
                  <div className="metric">
                    <FiDatabase />
                    <span>Queries/sec: 1.2M</span>
                  </div>
                  <div className="metric">
                    <FiZap />
                    <span>Latency: 0.3ms</span>
                  </div>
                  <div className="metric">
                    <FiTrendingUp />
                    <span>Data Points: 50B</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="container">
          <div className="cta-content">
            <h2>Ready for Agentic Database?</h2>
            <p>Experience the power of the industry's first agentic telemetry database.</p>
            <div className="cta-actions">
              <Link to="/pricing" className="btn btn-primary">
                Start Free Trial
              </Link>
              <Link to="/contact" className="btn btn-outline">
                Contact Sales
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AgenticDatabase;
